import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { NumberField } from '../ui/number-field';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Plus, Building2, Home, Car } from 'lucide-react';
import { formatDuration } from '../../utils/format';
import { CATEGORIES as CATEGORY_OBJS } from '../../lib/categories';

const SERVICE_TYPES = [
  { id: 'salon', name: 'Salon / Studio', icon: Building2 },
  { id: 'home', name: 'Home-based', icon: Home },
  { id: 'mobile', name: 'Mobile', icon: Car }
];

export function ServiceFormDialog({
  open,
  onOpenChange,
  serviceForm,
  setServiceForm,
  errors = {},
  editingService,
  providerServiceTypes,
  availableAddons,
  selectedAddonIds,
  setSelectedAddonIds,
  onServiceTypeToggle,
  onSave,
  // Co-Op gating: parent passes the Co-Op status + tier matrix (fetched
  // once from /api/co-op/status + /api/co-op/tiers). Null when the
  // provider isn't student-verified -- in that case we don't render the
  // Co-Op section at all.
  coOpStatus = null,
  coOpTiers = null,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Set the name, duration, price and category. Add Co-Op student pricing if you're verified.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {Object.keys(errors).length > 0 && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              data-testid="service-form-error-summary"
            >
              <p className="font-medium mb-1">Please fix the highlighted fields</p>
              <p className="text-red-700 text-xs">
                Required fields are marked with <span className="text-red-500">*</span>. Fill them in and try again.
              </p>
            </div>
          )}
          <div>
            <Label>Service Name <span className="text-red-500">*</span></Label>
            <Input
              value={serviceForm.name}
              onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
              placeholder="e.g., Full Body Massage"
              className={`input-zuriya ${errors.name ? 'border-red-400 focus:ring-red-200' : ''}`}
              data-testid="service-name-input"
            />
            {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="service-name-error">Service name is required</p>}
          </div>
          <div>
            <Label>Description <span className="text-red-500">*</span></Label>
            <Textarea
              value={serviceForm.description}
              onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
              placeholder="Describe your service..."
              className={`input-zuriya ${errors.description ? 'border-red-400 focus:ring-red-200' : ''}`}
              data-testid="service-description-input"
            />
            {errors.description && <p className="text-xs text-red-500 mt-1" data-testid="service-description-error">Description is required</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Duration <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <Select
                  value={String(Math.floor(serviceForm.duration / 60))}
                  onValueChange={(v) => setServiceForm(prev => ({...prev, duration: parseInt(v) * 60 + (prev.duration % 60)}))}
                >
                  <SelectTrigger className="input-zuriya" data-testid="service-duration-hours">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0,1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                      <SelectItem key={h} value={String(h)}>{h} hr{h !== 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(serviceForm.duration % 60)}
                  onValueChange={(v) => setServiceForm(prev => ({...prev, duration: Math.floor(prev.duration / 60) * 60 + parseInt(v)}))}
                >
                  <SelectTrigger className="input-zuriya" data-testid="service-duration-mins">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0,15,30,45].map(m => (
                      <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-stone-400 mt-1">{formatDuration(serviceForm.duration)}</p>
              {errors.duration && <p className="text-xs text-red-500 mt-1" data-testid="service-duration-error">Duration must be at least 15 minutes</p>}
            </div>
            <div>
              <Label>Price (£) <span className="text-red-500">*</span></Label>
              <NumberField
                value={
                  typeof serviceForm.price === 'number' && !Number.isNaN(serviceForm.price)
                    ? serviceForm.price
                    : null
                }
                onChange={(next) => setServiceForm({ ...serviceForm, price: next ?? '' })}
                min={0}
                step="0.01"
                allowDecimals
                placeholder="50"
                className={`input-zuriya ${errors.price ? 'border-red-400 focus:ring-red-200' : ''}`}
                testId="service-price-input"
              />
              {errors.price && <p className="text-xs text-red-500 mt-1" data-testid="service-price-error">Price must be greater than £0</p>}
            </div>
          </div>
          <div>
            <Label>Payment Requirement</Label>
            <Select value={serviceForm.payment_type} onValueChange={(v) => setServiceForm(prev => ({...prev, payment_type: v}))}>
              <SelectTrigger className="input-zuriya" data-testid="service-payment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full Payment Upfront</SelectItem>
                <SelectItem value="deposit_50">50% Deposit</SelectItem>
                <SelectItem value="minimum_deposit">Minimum Deposit</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-400 mt-1">
              {serviceForm.payment_type === 'full' && 'Client pays the full amount when booking.'}
              {serviceForm.payment_type === 'deposit_50' && 'Client pays 50% now, remaining 50% at appointment.'}
              {serviceForm.payment_type === 'minimum_deposit' && 'Client pays a minimum deposit, remaining at appointment.'}
            </p>
          </div>
          {serviceForm.payment_type === 'minimum_deposit' && (
            <div>
              <Label>Minimum Deposit Amount (£)</Label>
              <Input
                type="number"
                value={serviceForm.deposit_amount}
                onChange={(e) => setServiceForm({...serviceForm, deposit_amount: e.target.value})}
                placeholder="e.g. 20"
                className="input-zuriya"
                data-testid="service-deposit-input"
              />
              <p className="text-xs text-stone-400 mt-1">
                {serviceForm.price && serviceForm.deposit_amount
                  ? `Client pays £${parseFloat(serviceForm.deposit_amount).toFixed(2)} deposit, remaining £${(parseFloat(serviceForm.price) - parseFloat(serviceForm.deposit_amount)).toFixed(2)} at appointment`
                  : 'The minimum amount charged at booking time.'}
              </p>
            </div>
          )}
          <div>
            <Label>Category <span className="text-red-500">*</span></Label>
            <Select value={serviceForm.category} onValueChange={(v) => setServiceForm({...serviceForm, category: v})}>
              <SelectTrigger
                className={`input-zuriya ${errors.category ? 'border-red-400 focus:ring-red-200' : ''}`}
                data-testid="service-category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OBJS.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <p className="text-xs text-red-500 mt-1" data-testid="service-category-error">Category is required</p>}
          </div>
          <div>
            <Label className="mb-2 block">Where is this service available? <span className="text-red-500">*</span></Label>
            <div className="space-y-2">
              {(() => {
                // If the provider profile has service_types configured, scope
                // the list to those; otherwise fall back to all three options
                // so the form is never silently empty (previous bug: no
                // checkboxes rendered → Save button silently disabled).
                const visibleTypes = providerServiceTypes && providerServiceTypes.length > 0
                  ? SERVICE_TYPES.filter((type) => providerServiceTypes.includes(type.id))
                  : SERVICE_TYPES;
                return visibleTypes.map((type) => (
                <label
                  key={type.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    serviceForm.service_types.includes(type.id)
                      ? 'border-[#D4AF37] bg-[#F9F6F0]'
                      : 'border-[#E6E4DD] hover:border-[#D4AF37]'
                  }`}
                >
                  <Checkbox
                    checked={serviceForm.service_types.includes(type.id)}
                    onCheckedChange={() => onServiceTypeToggle(type.id)}
                  />
                  <type.icon className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-sm font-medium">{type.name}</span>
                </label>
                ));
              })()}
            </div>
            {(serviceForm.service_types.length === 0 || errors.service_types) && (
              <p className="text-xs text-red-500 mt-1" data-testid="service-types-error">Select at least one</p>
            )}
          </div>
          {availableAddons.length > 0 && (
            <div data-testid="inline-addons">
              <Label>Add-ons (optional)</Label>
              <p className="text-xs text-stone-400 mb-2">Attach add-ons clients can select at checkout</p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {availableAddons.map(addon => (
                  <label key={addon.addon_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F9F6F0] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAddonIds.includes(addon.addon_id)}
                      onChange={() => {
                        setSelectedAddonIds(prev =>
                          prev.includes(addon.addon_id)
                            ? prev.filter(id => id !== addon.addon_id)
                            : [...prev, addon.addon_id]
                        );
                      }}
                      className="rounded border-[#E6E4DD] text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-[#1A1A1A] flex-1">{addon.name}</span>
                    <span className="text-xs text-[#D4AF37] font-medium">+£{addon.price_modifier?.toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {coOpStatus?.is_student && coOpTiers && (
            <div
              className="rounded-lg border border-[#D4AF37]/40 bg-[#FAF8F2] p-3.5"
              data-testid="co-op-tier-section"
            >
              <Label className="flex items-center gap-2 text-[#1A1A1A]">
                Co-Op student tier
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#8a7320] bg-white px-1.5 py-0.5 rounded">
                  Optional
                </span>
              </Label>
              <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                Opt this service into the Student Skill-Exchange. Clients pay in credits plus a small cash top-up in person.
              </p>
              <div className="mt-3 space-y-2">
                <label className="flex items-start gap-2.5 p-2 rounded-md hover:bg-white cursor-pointer">
                  <input
                    type="radio"
                    name="co_op_tier"
                    value=""
                    checked={!serviceForm.co_op_tier}
                    onChange={() => setServiceForm({...serviceForm, co_op_tier: null})}
                    className="mt-1"
                    data-testid="co-op-tier-off"
                  />
                  <div className="text-sm">
                    <span className="text-[#1A1A1A] font-medium">Cash-only (default)</span>
                    <span className="block text-xs text-stone-500">Standard marketplace service, no Co-Op pricing.</span>
                  </div>
                </label>
                {Object.entries(coOpTiers).map(([key, t]) => (
                  <label
                    key={key}
                    className="flex items-start gap-2.5 p-2 rounded-md hover:bg-white cursor-pointer"
                    data-testid={`co-op-tier-${key}`}
                  >
                    <input
                      type="radio"
                      name="co_op_tier"
                      value={key}
                      checked={serviceForm.co_op_tier === key}
                      onChange={() => setServiceForm({...serviceForm, co_op_tier: key})}
                      className="mt-1"
                    />
                    <div className="text-sm">
                      <span className="text-[#1A1A1A] font-medium">
                        {t.label} <span className="text-[#D4AF37]">· {t.credits} credit{t.credits === 1 ? '' : 's'} + £{t.cash_gbp} cash</span>
                      </span>
                      <span className="block text-xs text-stone-500">{t.examples}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button 
            onClick={onSave} 
            className="w-full btn-primary" 
            data-testid="save-service-btn"
          >
            {editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
